/**
 * The form controls the seven Integrations panels are built from.
 *
 * They all ask the same handful of questions - a labelled pill, a secret whose
 * stored value never comes back to the browser, a native select, a switch, a
 * read-only URL somebody has to paste into a provider's console - so they ask
 * them once here instead of seven times. Written in the CRM's own vocabulary
 * (`crm-lab`, `crm-inp`, `crm-sub`) rather than the shared kit's `Label` and
 * `Input`, which draw the pre-redesign bordered box.
 *
 * Nothing in this file reads or logs a secret: `SecretPill` is an ordinary
 * password input whose placeholder stands in for a value the server keeps.
 */
import { useState, type ReactNode } from "react";
import { Check, Copy, Eye, EyeOff } from "lucide-react";
import { Switch, cn } from "@wa/ui";
import { IconBtn } from "../../../ui";

/**
 * A labelled control.
 *
 * `min-w-0` sits on the label itself, not only on whatever is inside it: a grid
 * track sizes to its content and the label IS the content, which is how a
 * number input elsewhere in this app once demanded 484px of a 0px track.
 */
export function Field({
  label,
  hint,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn("block min-w-0", className)}>
      <span className="crm-lab !mt-0">{label}</span>
      {children}
      {hint ? <span className="crm-sub !mt-1.5 block text-[11.5px] leading-snug">{hint}</span> : null}
    </label>
  );
}

/**
 * The same labelled control, for something that is a BUTTON rather than an input.
 *
 * `Field` is a `<label>`, and a label forwards its clicks to the control inside
 * it - which for a `MenuField` or a `PickerField` means the menu opens and shuts
 * again in one gesture, or never opens at all. So the label is a plain span here
 * and the control carries its own accessible name, the way `NoticeClipRow`'s
 * voice picker has always had to. Identical on screen, deliberately: a form of
 * pickers and inputs must not have two different-looking kinds of label in it.
 */
export function FieldBox({
  label,
  hint,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("block min-w-0", className)}>
      <span className="crm-lab !mt-0 block">{label}</span>
      {children}
      {hint ? <span className="crm-sub !mt-1.5 block text-[11.5px] leading-snug">{hint}</span> : null}
    </div>
  );
}

/** A named run of fields - "Identity", "Outbound", "Webhooks". */
export function FieldGroup({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("space-y-3", className)}>
      <h4 className="crm-lab !mt-0 uppercase tracking-[0.07em]">{title}</h4>
      {children}
    </section>
  );
}

interface PillProps {
  value: string | number;
  onChange?: (value: string) => void;
  type?: "text" | "email" | "password" | "number";
  placeholder?: string;
  autoComplete?: string;
  inputMode?: "numeric" | "email" | "tel" | "url";
  disabled?: boolean;
  readOnly?: boolean;
  /** Sids, keys and URLs read as one string of characters, not as words. */
  mono?: boolean;
  /** Centres the value. For a field whose whole content is one or two digits -
   *  `text-align` on the pill does not reach the input, which the user agent
   *  pins to `start` on its own. */
  centered?: boolean;
  min?: number;
  id?: string;
  ariaLabel?: string;
  /** Selects the whole value on focus, for a field whose only use is copying. */
  selectOnFocus?: boolean;
  /** Sits inside the pill, after the input - a reveal eye or a copy disc. */
  trailing?: ReactNode;
  /** Overrides the pill's own height, width or padding - a row of them packed
   *  into a list is not the same control as one standing under a label. */
  className?: string;
  /**
   * Id of a `<datalist>` to suggest from, for a field that is genuinely free
   * text but has a known set of usual answers.
   *
   * A suggestion rather than a select, because the two failures are not
   * symmetric: a name that has never been used yet must still be typeable, and
   * a name that differs from the one in use by a letter is a row no saved
   * filter ever finds.
   */
  list?: string;
}

/**
 * The sunken pill every text field in this section wears.
 *
 * A `span`, not a `label`: it is always rendered inside `Field`'s label and
 * nesting one label in another is invalid HTML.
 */
export function Pill({
  value,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
  inputMode,
  disabled,
  readOnly,
  mono,
  centered,
  min,
  id,
  ariaLabel,
  selectOnFocus,
  trailing,
  className,
  list,
}: PillProps) {
  return (
    <span className={cn("crm-inp h-11", className)}>
      <input
        id={id}
        type={type}
        list={list}
        value={value}
        min={min}
        inputMode={inputMode}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        readOnly={readOnly}
        aria-label={ariaLabel}
        className={cn(mono && "font-mono text-[12.25px]", centered && "text-center")}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onFocus={selectOnFocus ? (e) => e.target.select() : undefined}
      />
      {trailing}
    </span>
  );
}

/**
 * A credential field.
 *
 * The stored value is never sent to the browser - the placeholder is the whole
 * report that one exists - so an empty box means "keep what is saved" rather
 * than "clear it". `reveal` adds an eye only where the panel already had one;
 * a field that never shows its contents does not grow the ability to.
 */
export function SecretPill({
  value,
  onChange,
  placeholder,
  disabled,
  reveal,
  showLabel,
  hideLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  reveal?: boolean;
  showLabel?: string;
  hideLabel?: string;
}) {
  const [shown, setShown] = useState(false);

  return (
    <Pill
      value={value}
      onChange={onChange}
      type={reveal && shown ? "text" : "password"}
      placeholder={placeholder}
      autoComplete="new-password"
      disabled={disabled}
      trailing={
        reveal ? (
          <IconBtn
            small
            className="-mr-2.5"
            tabIndex={-1}
            onClick={() => setShown((v) => !v)}
            aria-label={shown ? hideLabel : showLabel}
            title={shown ? hideLabel : showLabel}
          >
            {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </IconBtn>
        ) : undefined
      }
    />
  );
}

/** A value the operator has to carry somewhere else: read-only, with the disc
 *  that puts it on the clipboard. */
export function CopyPill({
  value,
  copyLabel,
  copiedLabel,
}: {
  value: string;
  copyLabel: string;
  copiedLabel: string;
}) {
  const [done, setDone] = useState(false);

  function copy() {
    if (!value) return;
    navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setDone(true);
        setTimeout(() => setDone(false), 2000);
      })
      .catch(() => {});
  }

  return (
    <Pill
      value={value}
      readOnly
      mono
      selectOnFocus
      ariaLabel={copyLabel}
      trailing={
        <IconBtn
          small
          className="-mr-2.5"
          onClick={copy}
          title={done ? copiedLabel : copyLabel}
          aria-label={done ? copiedLabel : copyLabel}
        >
          {done ? (
            <Check className="size-4" style={{ color: "var(--color-success)" }} />
          ) : (
            <Copy className="size-4" />
          )}
        </IconBtn>
      }
    />
  );
}

/** A native select in the same pill. Native rather than a Radix menu for the
 *  same reason the toolbar's `SelectField` is: inside a form the OS control
 *  keeps keyboard and mobile behaviour for free. */
/**
 * The settings panel's dropdown.
 *
 * Re-exported rather than defined here: it used to be a native `<select>` in a
 * pill, and it is now the app's Radix one (`crm/ui/SelectMenu`), which every
 * other dropdown in the CRM also wears. The name stays because ten panels ask
 * for it by it.
 */
export { SelectPill } from "../../../ui";


/** A boolean, said as a switch rather than as a checkbox: these are settings
 *  that take effect, not items being ticked off a list. */
export function ToggleRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex min-w-0 items-start gap-3">
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} className="mt-0.5" />
      <span className="min-w-0">
        <span className="block text-[13px] font-[550]">{label}</span>
        {hint ? <span className="crm-sub !mt-0.5 block text-[11.5px] leading-snug">{hint}</span> : null}
      </span>
    </label>
  );
}

export type NoteTone = "ok" | "warn" | "bad" | "info" | "mut";

/* Colour expressions rather than class names, the same way `Kpi` states its
   tones: the tint is derived from the theme's own success/warning/destructive
   rather than from a hand-picked green, and nothing here depends on a content
   scan having seen the class written out. */
const NOTE_BG: Record<NoteTone, string> = {
  ok: "color-mix(in oklab, var(--color-success) 12%, var(--crm-surface))",
  warn: "color-mix(in oklab, var(--color-warning) 14%, var(--crm-surface))",
  bad: "color-mix(in oklab, var(--color-destructive) 11%, var(--crm-surface))",
  info: "color-mix(in oklab, var(--color-primary) 10%, var(--crm-surface))",
  mut: "var(--crm-sunk)",
};

const NOTE_FG: Record<NoteTone, string> = {
  ok: "color-mix(in oklab, var(--color-success) 80%, var(--color-foreground))",
  warn: "color-mix(in oklab, var(--color-warning) 52%, var(--color-foreground))",
  bad: "var(--color-destructive)",
  info: "color-mix(in oklab, var(--color-primary) 84%, var(--color-foreground))",
  mut: "var(--color-muted-foreground)",
};

/**
 * A sentence the panel has to say about itself: where a credential came from,
 * why nothing is being sent, what a test replied.
 *
 * Flat rather than `crm-alert`, which is a card with its own shadow - correct
 * for a failure announced on the page, wrong for a line inside a card that
 * already has one.
 */
export function Note({
  tone,
  icon,
  children,
  className,
  live,
}: {
  tone: NoteTone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Announce it when it appears - the result of a test the operator ran. */
  live?: boolean;
}) {
  return (
    <div
      className={cn("flex items-start gap-2.5 rounded-[20px] px-4 py-3 text-[12.75px] leading-relaxed", className)}
      style={{ background: NOTE_BG[tone], color: NOTE_FG[tone] }}
      role={live ? "status" : undefined}
      aria-live={live ? "polite" : undefined}
    >
      {icon}
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}

/** The row a form ends on: the save button, whatever else acts on the form, and
 *  the pill that says how the last attempt went. */
export function SaveRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-[var(--crm-hair)] pt-5">{children}</div>
  );
}
